import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import KeyRounded from '@mui/icons-material/KeyRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import NotificationBell from './NotificationBell';

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

const PUBLIC = process.env.PUBLIC_URL;

/**
 * Sticky top bar: brand lockup, the day-to-day section tabs, and the user
 * menu - which holds the occasional things: profile, pay and letters.
 */
export default function AppHeader({ me, tabs, current, onTab, onChangePassword, onSignOut, onOpenTicket, onOpenPay, onOpenProfile }) {
  const [anchor, setAnchor] = useState(null);
  const name = me.name || me.username;
  const sub = [me.employeeCode, me.designation].filter(Boolean).join(' · ');

  return (
    <AppBar position="sticky" color="inherit" elevation={0}
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'rgba(255,255,255,0.92)',
                  backdropFilter: 'saturate(180%) blur(8px)' }}>
      {/* thin navy-to-teal rule echoing the logo's two-tone wordmark */}
      <Box sx={{ height: 3, background: 'linear-gradient(90deg, #123871 0%, #123871 55%, #009da1 100%)' }} />
      <Toolbar sx={{ gap: { xs: 1, md: 2.5 }, minHeight: { xs: 60 }, px: { xs: 1.5, sm: 2.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, flexShrink: 0 }}>
          <Box component="img" src={`${PUBLIC}/brand/logo-mark.svg`} alt="" sx={{ height: 30 }} />
          <Box component="img" src={`${PUBLIC}/brand/logo-wordmark.svg`} alt="Cloudmyle"
               sx={{ height: 12, display: { xs: 'none', md: 'block' } }} />
          <Typography component="span"
                      sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: 'secondary.main',
                            pl: 1.1, borderLeft: 1, borderColor: 'divider', lineHeight: '16px' }}>
            WORKDAY
          </Typography>
        </Box>

        <Tabs value={current} onChange={(_, v) => onTab(v)} variant="scrollable" scrollButtons="auto"
              allowScrollButtonsMobile aria-label="Sections"
              sx={{ flex: 1, minWidth: 0, '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' } }}>
          {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
        </Tabs>

        {me.linked && onOpenTicket && <NotificationBell onOpenTicket={onOpenTicket} onOpenPay={onOpenPay} />}

        <ButtonBase onClick={(e) => setAnchor(e.currentTarget)} aria-haspopup="menu"
                    aria-label={`Account menu for ${name}`}
                    sx={{ borderRadius: 2, px: 1, py: 0.5, gap: 1.2, flexShrink: 0,
                          '&:hover': { bgcolor: 'action.hover' } }}>
          <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' }, maxWidth: 190, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>{name}</Typography>
            <Typography noWrap sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
              {sub}{me.isAdmin ? ' · HR' : ''}
            </Typography>
          </Box>
          <Avatar sx={{ width: 34, height: 34, fontSize: 13, fontWeight: 700,
                        bgcolor: 'primary.main' }}>{initials(name)}</Avatar>
          <KeyboardArrowDown fontSize="small" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }} />
        </ButtonBase>

        <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { mt: 1, minWidth: 230, borderRadius: 2.5 } } }}>
          <Box sx={{ px: 2, py: 1.2 }}>
            <Typography sx={{ fontWeight: 650, fontSize: 14 }}>{name}</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{me.username}</Typography>
            {sub && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{sub}</Typography>}
          </Box>
          <Divider />
          {onOpenProfile && (
            <MenuItem onClick={() => { setAnchor(null); onOpenProfile(); }}>
              <ListItemIcon><PersonOutlineRounded fontSize="small" /></ListItemIcon>
              My profile
            </MenuItem>
          )}
          {onOpenPay && (
            <MenuItem onClick={() => { setAnchor(null); onOpenPay(); }}>
              <ListItemIcon><ReceiptLongRounded fontSize="small" /></ListItemIcon>
              Pay & documents
            </MenuItem>
          )}
          {(onOpenProfile || onOpenPay) && <Divider />}
          <MenuItem onClick={() => { setAnchor(null); onChangePassword(); }}>
            <ListItemIcon><KeyRounded fontSize="small" /></ListItemIcon>
            Change password
          </MenuItem>
          <MenuItem onClick={() => { setAnchor(null); onSignOut(); }}>
            <ListItemIcon><LogoutRounded fontSize="small" /></ListItemIcon>
            Sign out
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
